import { apiFetch } from './client';

/**
 * API keys — management client for the public REST API (billable 'api'
 * feature). The plaintext key is returned ONCE by create() and never again;
 * afterwards only key_prefix identifies it.
 */

export type ApiKeyTier = 'platform' | 'reseller' | 'customer';

export interface ApiKeyRow {
  id: string;
  reseller_id: string | null;
  customer_id: string | null;
  name: string;
  /** First 8 chars of the plaintext ('ivk_a1b2') — display only. */
  key_prefix: string | null;
  scopes: string[];
  rate_limit_per_min: number;
  last_used_at: string | null;
  revoked_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  tier: ApiKeyTier;
}

export interface CreateApiKeyBody {
  name: string;
  scopes: string[];
  rate_limit_per_min?: number;
  /** reseller_owner / super_admin — mint a customer-tier key. */
  customer_id?: string;
  /** super_admin — mint a reseller-tier key. */
  reseller_id?: string;
}

export interface CreateApiKeyResponse {
  api_key: ApiKeyRow;
  /** Shown exactly once. Not stored anywhere server-side. */
  plaintext: string;
}

/**
 * Mirror of backend/src/apiKeys/scopes.ts (minus the platform-only
 * 'platform' wildcard, which the backend adds implicitly when a
 * super_admin mints a platform key). Keep in sync when adding scopes.
 */
export interface ScopeDef {
  scope: string;
  label: string;
  description: string;
  kind: 'read' | 'write';
}

export const API_SCOPE_CATALOG: ScopeDef[] = [
  { scope: 'recordings:read', label: 'Recordings', description: 'List and fetch recording metadata.', kind: 'read' },
  { scope: 'ivr:read', label: 'IVR trees', description: 'List trees and read their node graphs.', kind: 'read' },
  { scope: 'callers:read', label: 'Callers', description: 'List caller profiles.', kind: 'read' },
  { scope: 'calls:read', label: 'Call log', description: 'Read call sessions, including live calls.', kind: 'read' },
  { scope: 'voicemails:read', label: 'Voicemail', description: 'List voicemail boxes and messages.', kind: 'read' },
  { scope: 'sms:read', label: 'SMS log', description: 'Read the SMS message log.', kind: 'read' },
  { scope: 'payments:read', label: 'Payments', description: 'Read payment transactions.', kind: 'read' },
  { scope: 'invoices:read', label: 'Invoices', description: 'Read custom-billing invoices.', kind: 'read' },
  { scope: 'dids:read', label: 'Phone numbers', description: 'List phone numbers (DIDs).', kind: 'read' },
  { scope: 'callers:write', label: 'Create callers', description: 'Create caller profiles.', kind: 'write' },
  { scope: 'sms:send', label: 'Send SMS', description: 'Queue outbound SMS messages.', kind: 'write' },
];

export const apiKeys = {
  list: (params: { customer_id?: string; include_revoked?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.customer_id) q.set('customer_id', params.customer_id);
    if (params.include_revoked) q.set('include_revoked', 'true');
    const qs = q.toString();
    return apiFetch<{ rows: ApiKeyRow[] }>(`/api/api-keys${qs ? `?${qs}` : ''}`);
  },

  create: (body: CreateApiKeyBody) =>
    apiFetch<CreateApiKeyResponse>('/api/api-keys', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  revoke: (id: string) =>
    apiFetch<{ api_key: ApiKeyRow }>(`/api/api-keys/${id}`, { method: 'DELETE' }),
};
